import FilterListIcon from "@mui/icons-material/FilterList";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	FormControl,
	Grid,
	MenuItem,
	Pagination,
	Paper,
	Select,
	Typography,
} from "@mui/material";
import { fetchNotificationCenterIndex } from "actions/notificationCenterActions";
import dayjs from "dayjs";
import { selectCustomerDetails } from "features/customer";
import {
	selectNotificationCenter,
	selectNotificationCenterStats,
	selectPaginatedNotifications,
	setCurrentPage,
	setSelectedFilter,
} from "features/notificationCenter";
import { useAppSelector, useAuth } from "hooks/hooks";
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { NotificationCenterItem } from "types";
import styles from "./NotificationCenter.module.scss";

const NotificationCenter: React.FC = () => {
	const { dispatch, auth } = useAuth();
	const navigate = useNavigate();

	const notificationCenterState = useAppSelector(selectNotificationCenter);
	const paginatedNotifications = useAppSelector(selectPaginatedNotifications);
	const stats = useAppSelector(selectNotificationCenterStats);
	const customerInfo = useAppSelector(selectCustomerDetails);

	const { isLoading, isFetching, selectedFilter } = notificationCenterState;

	// Filter options based on customer enabled products
	const filterOptions = useMemo(() => {
		const options = [{ value: "all", label: "All" }];

		if (customerInfo?.yourItSystemsEnabled) {
			options.push({ value: "yits", label: "Your IT Systems" });
		}

		if (customerInfo?.yourEmployeesEnabled) {
			options.push({ value: "itye", label: "Your Employees" });
		}

		// Common is always available
		options.push({ value: "common", label: "Common" });

		return options;
	}, [customerInfo?.yourItSystemsEnabled, customerInfo?.yourEmployeesEnabled]);

	// Filter notifications based on customer's enabled products
	const allowedNotifications = useMemo(() => {
		if (!Array.isArray(paginatedNotifications)) {
			return [];
		}

		return paginatedNotifications.filter((notification) => {
			if (!notification || !Array.isArray(notification.products)) {
				return false;
			}

			const hasCommon = notification.products.some(
				(p) => typeof p === "string" && p.toLowerCase().includes("common"),
			);
			const hasYITS = notification.products.some(
				(p) => typeof p === "string" && p.toLowerCase().includes("yits"),
			);
			const hasITYE = notification.products.some(
				(p) => typeof p === "string" && p.toLowerCase().includes("itye"),
			);

			if (hasCommon) {
				return true;
			}
			if (hasYITS && customerInfo?.yourItSystemsEnabled) {
				return true;
			}
			if (hasITYE && customerInfo?.yourEmployeesEnabled) {
				return true;
			}

			return false;
		});
	}, [
		paginatedNotifications,
		customerInfo?.yourItSystemsEnabled,
		customerInfo?.yourEmployeesEnabled,
	]);

	useEffect(() => {
		if (isLoading) {
			dispatch(fetchNotificationCenterIndex({ auth }));
		}
	}, [dispatch, isLoading, auth]);

	const handleFilterChange = (filter: string) => {
		dispatch(setSelectedFilter(filter));
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		dispatch(setCurrentPage(page));
	};

	const handleNotificationClick = (notification: NotificationCenterItem) => {
		navigate(`/notifications/${notification.file.replace(".md", "")}`);
	};

	const getTypeColor = (type: string) => {
		switch (type) {
			case "notification":
				return "primary";
			case "newsletter":
				return "info";
			case "recommendation":
				return "warning";
			case "securityalert":
				return "error";
			case "releasenote":
				return "success";
			default:
				return "default";
		}
	};

	const getTypeLabel = (type: string) => {
		switch (type) {
			case "notification":
				return "Notification";
			case "newsletter":
				return "Newsletter";
			case "recommendation":
				return "Recommendation";
			case "securityalert":
				return "Security Alert";
			case "releasenote":
				return "Release Note";
			default:
				return type;
		}
	};

	if (isLoading) {
		return (
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "400px" }}
			>
				<CircularProgress />
			</Grid>
		);
	}

	return (
		<Grid container spacing={3} className={styles.container}>
			{/* Header */}
			<Grid item xs={12}>
				<Box display="flex" alignItems="center" mb={2}>
					<NotificationsIcon sx={{ mr: 1, color: "primary.main" }} />
					<Typography variant="h4" component="h1">
						Notifications
					</Typography>
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Stay updated with the latest news, features, and important information.
				</Typography>
			</Grid>

			{/* Filters */}
			<Grid item xs={12}>
				<Paper elevation={1} sx={{ p: 2, mb: 2 }}>
					<Box display="flex" alignItems="center" gap={2}>
						<FilterListIcon color="action" />
						<Typography variant="body2" color="text.secondary">
							Filter by product:
						</Typography>
						<FormControl size="small" sx={{ minWidth: 200 }}>
							<Select
								value={selectedFilter}
								onChange={(e) => handleFilterChange(e.target.value)}
								displayEmpty
							>
								{filterOptions.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Typography variant="body2" color="text.secondary">
							{stats.totalFiltered} items found
						</Typography>
					</Box>
				</Paper>
			</Grid>

			{/* Notifications List */}
			<Grid item xs={12}>
				{allowedNotifications.length === 0 ? (
					<Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
						<NotificationsIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
						<Typography variant="h6" color="text.secondary">
							No notifications found
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{selectedFilter !== "all"
								? "Try adjusting your filter or check back later for updates."
								: "Check back later for updates and announcements."}
						</Typography>
					</Paper>
				) : (
					<Grid container spacing={2}>
						{allowedNotifications.map((notification) => (
							<Grid item xs={12} key={notification.file}>
								<Card
									className={styles.notificationCard}
									onClick={() => handleNotificationClick(notification)}
									sx={{
										cursor: "pointer",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											transform: "translateY(-2px)",
											boxShadow: 3,
										},
									}}
								>
									<CardContent>
										<Box
											display="flex"
											justifyContent="space-between"
											alignItems="flex-start"
											mb={1}
										>
											<Typography
												variant="h6"
												component="h2"
												sx={{ fontWeight: 600 }}
											>
												{notification.title}
											</Typography>
											<Chip
												label={getTypeLabel(notification.type)}
												color={getTypeColor(notification.type) as any}
												size="small"
											/>
										</Box>

										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 2 }}
										>
											{dayjs(notification.date).format("MMMM D, YYYY")}
										</Typography>

										<Box display="flex" gap={1} flexWrap="wrap">
											{notification.products.map((product) => (
												<Chip
													key={product}
													label={product}
													variant="outlined"
													size="small"
													color="default"
												/>
											))}
										</Box>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				)}
			</Grid>

			{/* Pagination */}
			{allowedNotifications.length > 0 && stats.totalPages > 1 && (
				<Grid item xs={12}>
					<Box display="flex" justifyContent="center" mt={3}>
						<Pagination
							count={stats.totalPages}
							page={stats.currentPage}
							onChange={handlePageChange}
							color="primary"
							size="large"
						/>
					</Box>
					<Typography
						variant="body2"
						color="text.secondary"
						textAlign="center"
						sx={{ mt: 1 }}
					>
						Showing {stats.startIndex}-{stats.endIndex} of {stats.totalFiltered}{" "}
						notifications
					</Typography>
				</Grid>
			)}

			{isFetching && (
				<Grid item xs={12}>
					<Box display="flex" justifyContent="center" mt={2}>
						<CircularProgress size={24} />
					</Box>
				</Grid>
			)}
		</Grid>
	);
};

export default NotificationCenter;
